'use client'

import React, { useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { ResourceHubResultsViewProps } from '../../../models/organisms/ResourceHub'
import Icon from '../../atoms/Icon/Icon.component'
import ResourceHubCard from './ResourceHubCard.component'
import * as S from './ResourceHub.styles'
import Button from '../../atoms/Button/Button.component'

type PaginationEntry = number | 'dots'

function buildPagesToShow(
  currentPage: number,
  totalPages: number,
  siblingCount = 2
): PaginationEntry[] {
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)

  const withDots = pages.map((page) => {
    if (page === 1 || page === totalPages) return page
    if (page >= currentPage - siblingCount && page <= currentPage + siblingCount) return page
    return 'dots' as const
  })

  return withDots.reduce<PaginationEntry[]>((acc, entry) => {
    if (entry === 'dots' && acc[acc.length - 1] === 'dots') return acc
    return [...acc, entry]
  }, [])
}

const ResourceHubResultsView: React.FC<ResourceHubResultsViewProps> = ({
  items,
  isLoading,
  isError,
  totalPages,
  hasNextPage,
  hasPrevPage,
  nextPage,
  prevPage,
  goToPage,
  currentPage,
  colorScheme,
  noResultsButton,
}) => {
  const t = useTranslations('resourceHub')

  const paginationPages = useMemo(
    () => buildPagesToShow(currentPage, totalPages),
    [currentPage, totalPages]
  )

  return (
    <S.ResourceHubContent>
      {isError ? (
        <S.ResourceHubError role="alert">{t('errorLoadingResources')}</S.ResourceHubError>
      ) : isLoading ? (
        <S.ResourceHubLoading role="status" aria-live="polite">
          {t('loadingResources')}
        </S.ResourceHubLoading>
      ) : items.length === 0 ? (
        <S.ResourceHubEmpty role="status" aria-live="polite">
          <p>{t('noResourcesFound')}<br />{t('noResourcesFoundDescription')}</p>
          {noResultsButton && (
            <Button
              {...noResultsButton}
              variant="primary"
              iconEnd="arrow-right"
              inheritedColorScheme={colorScheme}
            />
          )}
        </S.ResourceHubEmpty>
      ) : (
        <>
          <S.ResourceHubGrid>
            {items.map((item) => (
              <ResourceHubCard key={item.id} item={item} colorScheme={colorScheme} />
            ))}
          </S.ResourceHubGrid>

          {totalPages > 0 && (
            <S.ResourceHubPagination aria-label={t('pagination')}>
              <S.PaginationButton
                type="button"
                onClick={prevPage}
                disabled={!hasPrevPage}
                aria-label={t('previous')}
                colorScheme={colorScheme}
              >
                <Icon name="chevron-left" aria-hidden />
              </S.PaginationButton>

              {paginationPages.map((entry, index) =>
                entry === 'dots' ? (
                  <S.PaginationDots key={`dots-${index}`} aria-hidden>
                    &hellip;
                  </S.PaginationDots>
                ) : (
                  <S.PaginationButton
                    key={`pagination-button-${entry}`}
                    type="button"
                    $active={entry === currentPage}
                    onClick={() => goToPage(entry)}
                    aria-label={`${t('pageLabel')} ${entry}`}
                    aria-current={entry === currentPage ? 'page' : undefined}
                    colorScheme={colorScheme}
                  >
                    {entry}
                  </S.PaginationButton>
                )
              )}

              <S.PaginationButton
                type="button"
                onClick={nextPage}
                disabled={!hasNextPage}
                aria-label={t('next')}
                colorScheme={colorScheme}
              >
                <Icon name="chevron-right" aria-hidden />
              </S.PaginationButton>
            </S.ResourceHubPagination>
          )}
        </>
      )}
    </S.ResourceHubContent>
  )
}

export default ResourceHubResultsView
